import { supabase } from "@/lib/supabase";
import { isStoragePath, resolveAssetUrls } from "@/lib/mediaStorage";
import type { ExperienceDetail, GalleryImage, ExperienceFaq } from "@/mocks/experienceDetails";
import type { ExperienceFacets } from "@/mocks/experienceCatalog";
import type { Farm } from "@/mocks/farms";
import type { FarmerProfile } from "@/mocks/farmerProfiles";
import type { Testimonial } from "@/mocks/testimonials";
import { experiences as mockExperienceSummaries } from "@/mocks/siteData";
import { experienceDetails as mockExperienceDetails } from "@/mocks/experienceDetails";
import { experienceFacets as mockExperienceFacets } from "@/mocks/experienceCatalog";
import { isFarmOpen, publicFarms, reopeningDateFor } from "@/mocks/farms";
import { farmerProfiles as mockFarmers } from "@/mocks/farmerProfiles";
import { testimonials as mockTestimonials } from "@/mocks/testimonials";

/* --------------------------------- shared ---------------------------------- */

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
}

function asGallery(value: unknown): GalleryImage[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const record = (entry || {}) as Record<string, unknown>;
      const src =
        typeof record.src === "string"
          ? (record.src as string)
          : typeof record.url === "string"
            ? (record.url as string)
            : "";
      const alt = typeof record.alt === "string" ? (record.alt as string) : "";
      return { src, alt };
    })
    .filter((image) => image.src);
}

function asFaqs(value: unknown): ExperienceFaq[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const record = (entry || {}) as Record<string, unknown>;
      const q = String(record.q ?? record.question ?? "").trim();
      const a = String(record.a ?? record.answer ?? "").trim();
      return { q, a };
    })
    .filter((faq) => faq.q);
}

function formatMonth(value: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** Collects every storage-path asset value from a set of mapped records. */
function collectPaths(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => isStoragePath(value))));
}

async function resolveMap(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  return resolveAssetUrls(paths);
}

const swap = (value: string, map: Record<string, string>): string =>
  isStoragePath(value) && map[value] ? map[value] : value;

/* ------------------------------- experiences ------------------------------- */

export interface ExperienceSummary {
  slug: string;
  emoji: string;
  title: string;
  tagline: string;
  description: string;
  highlights: string[];
  perfectFor: string;
}

export interface PublicExperience {
  summary: ExperienceSummary;
  detail: ExperienceDetail;
  facets: ExperienceFacets;
}

interface ExperienceRow {
  name: string;
  slug: string | null;
  category: string | null;
  description: string | null;
  duration: string | null;
  age_range: string | null;
  audience: string | null;
  highlights: unknown;
  image: string | null;
  emoji: string | null;
  tagline: string | null;
  badge: string | null;
  hero_image: string | null;
  group_size: string | null;
  best_season: string | null;
  location: string | null;
  price_from: string | null;
  perfect_for: string | null;
  overview: unknown;
  what_you_will_do: unknown;
  includes_list: unknown;
  good_to_know: unknown;
  gallery: unknown;
  faqs: unknown;
  audiences: unknown;
  seasons: unknown;
  durations: unknown;
}

function mapExperienceRow(row: ExperienceRow): PublicExperience {
  const slug = row.slug || row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const hero = row.hero_image || row.image || "";
  const gallery = asGallery(row.gallery);
  const emptyFacets: ExperienceFacets = { audiences: [], seasons: [], durations: [] };

  const detail: ExperienceDetail = {
    slug,
    title: row.name,
    emoji: row.emoji || "🌿",
    tagline: row.tagline || row.category || "",
    badge: row.badge || row.category || "",
    heroImage: hero,
    duration: row.duration || "",
    groupSize: row.group_size || "",
    bestSeason: row.best_season || "",
    location: row.location || "",
    priceFrom: row.price_from || "",
    overview: asStringArray(row.overview),
    gallery,
    whatYouWillDo: asStringArray(row.what_you_will_do),
    includes: asStringArray(row.includes_list),
    goodToKnow: asStringArray(row.good_to_know),
    faqs: asFaqs(row.faqs),
  };

  const summary: ExperienceSummary = {
    slug,
    emoji: detail.emoji,
    title: row.name,
    tagline: detail.tagline,
    description: row.description || "",
    highlights: asStringArray(row.highlights),
    perfectFor: row.perfect_for || row.audience || "",
  };

  const facets: ExperienceFacets = {
    audiences: asStringArray(row.audiences),
    seasons: asStringArray(row.seasons),
    durations: asStringArray(row.durations),
  };

  return {
    summary,
    detail,
    facets:
      facets.audiences.length || facets.seasons.length || facets.durations.length
        ? facets
        : emptyFacets,
  };
}

function mockExperiences(): PublicExperience[] {
  return mockExperienceSummaries
    .map((exp) => ({
      summary: {
        slug: exp.slug,
        emoji: exp.emoji,
        title: exp.title,
        tagline: exp.tagline,
        description: exp.description,
        highlights: exp.highlights,
        perfectFor: exp.perfectFor,
      } as ExperienceSummary,
      detail: mockExperienceDetails[exp.slug] as ExperienceDetail,
      facets: (mockExperienceFacets[exp.slug] as ExperienceFacets) || {
        audiences: [],
        seasons: [],
        durations: [],
      },
    }))
    .filter((entry) => Boolean(entry.detail));
}

export async function fetchPublicExperiences(): Promise<PublicExperience[]> {
  try {
    const { data, error } = await supabase
      .from("experiences")
      .select("*")
      .eq("status", "published")
      .order("id", { ascending: true });
    if (error) throw error;

    const rows = (data as ExperienceRow[] | null) || [];
    if (rows.length === 0) return mockExperiences();

    const mapped = rows.map(mapExperienceRow);
    const paths = collectPaths([
      ...mapped.map((entry) => entry.detail.heroImage),
      ...mapped.flatMap((entry) => entry.detail.gallery.map((image) => image.src)),
    ]);
    const map = await resolveMap(paths);

    return mapped.map((entry) => ({
      ...entry,
      detail: {
        ...entry.detail,
        heroImage: swap(entry.detail.heroImage, map),
        gallery: entry.detail.gallery.map((image) => ({
          ...image,
          src: swap(image.src, map),
        })),
      },
    }));
  } catch {
    return mockExperiences();
  }
}

/* ---------------------------------- farms ---------------------------------- */

interface FarmRow {
  slug: string | null;
  name: string;
  farmer_name: string | null;
  village: string | null;
  district: string | null;
  crop: string | null;
  area: string | null;
  established: string | null;
  summary: string | null;
  highlights: unknown;
  types: unknown;
  image: string | null;
  photos: unknown;
  map_query: string | null;
  experience_slug: string | null;
  experience_label: string | null;
  status: string | null;
  /** Optional on the record: absent until the farm has an expected return date. */
  reopening_date?: string | null;
}

function mapFarmRow(row: FarmRow): Farm {
  const photoGallery = asGallery(row.photos);
  const slug = row.slug || "";
  return {
    slug,
    name: row.name,
    farmer: row.farmer_name || "",
    village: row.village || "",
    district: row.district || "",
    crop: row.crop || "",
    area: row.area || "",
    established: row.established || "",
    summary: row.summary || "",
    highlights: asStringArray(row.highlights),
    types: asStringArray(row.types),
    image: row.image || "",
    photos: photoGallery.map((image) => image.src),
    photoAlts: photoGallery.map((image) => image.alt),
    mapQuery: row.map_query || "",
    experienceSlug: row.experience_slug || "",
    experienceLabel: row.experience_label || "",
    // `temporarily_closed` is not hidden: the farm stays on the site with a
    // "reopening soon" notice so visitors can still discover it, but nothing on
    // the page will offer a booking.
    availability: row.status === "temporarily_closed" ? "reopening_soon" : "open",
    // A date stored on the farm record wins; otherwise fall back to the curated
    // date for that slug so a closed farm can still say when to expect it back.
    reopeningBy: (row.reopening_date || reopeningDateFor(slug)).slice(0, 10),
  };
}

export async function fetchPublicFarms(): Promise<Farm[]> {
  try {
    // A farm is public when it is `active` OR `temporarily_closed`. The second
    // group is published with a "reopening soon" notice instead of being hidden:
    // it keeps existing links and search results working while refusing bookings.
    // `pending_approval` never went live and is not public at all.
    const { data, error } = await supabase
      .from("farms")
      .select("*")
      .in("status", ["active", "temporarily_closed"])
      .order("id", { ascending: true });
    if (error) throw error;

    const rows = (data as FarmRow[] | null) || [];
    if (rows.length === 0) return publicFarms;

    // Open farms first so a closed farm never leads the grid or the map.
    const mapped = rows
      .map(mapFarmRow)
      .sort((a, b) => Number(isFarmOpen(b)) - Number(isFarmOpen(a)));
    const paths = collectPaths([
      ...mapped.map((farm) => farm.image),
      ...mapped.flatMap((farm) => farm.photos),
    ]);
    const map = await resolveMap(paths);

    return mapped.map((farm) => ({
      ...farm,
      image: swap(farm.image, map),
      photos: farm.photos.map((photo) => swap(photo, map)),
    }));
  } catch {
    return publicFarms;
  }
}

/* --------------------------------- farmers --------------------------------- */

interface FarmerRow {
  name: string;
  slug: string | null;
  village: string | null;
  crop: string | null;
  farming_for: string | null;
  quote: string | null;
  intro: string | null;
  bio: unknown;
  specialties: unknown;
  image: string | null;
  portrait: string | null;
  experience_slug: string | null;
  experience_label: string | null;
  gallery: unknown;
}

function mapFarmerRow(row: FarmerRow): FarmerProfile {
  return {
    slug: row.slug || "",
    name: row.name,
    village: row.village || "",
    crop: row.crop || "",
    farmingFor: row.farming_for || "",
    quote: row.quote || "",
    portrait: row.portrait || row.image || "",
    intro: row.intro || "",
    bio: asStringArray(row.bio),
    specialties: asStringArray(row.specialties),
    experienceSlug: row.experience_slug || "",
    experienceLabel: row.experience_label || "",
    gallery: asGallery(row.gallery),
  };
}

export async function fetchPublicFarmers(): Promise<FarmerProfile[]> {
  try {
    const { data, error } = await supabase
      .from("farmers")
      .select("*")
      .not("experience_slug", "is", null)
      .order("id", { ascending: true });
    if (error) throw error;

    const rows = (data as FarmerRow[] | null) || [];
    if (rows.length === 0) return mockFarmers;

    const mapped = rows.map(mapFarmerRow);
    const paths = collectPaths([
      ...mapped.map((farmer) => farmer.portrait),
      ...mapped.flatMap((farmer) => farmer.gallery.map((image) => image.src)),
    ]);
    const map = await resolveMap(paths);

    return mapped.map((farmer) => ({
      ...farmer,
      portrait: swap(farmer.portrait, map),
      gallery: farmer.gallery.map((image) => ({ ...image, src: swap(image.src, map) })),
    }));
  } catch {
    return mockFarmers;
  }
}

/* --------------------------------- reviews --------------------------------- */

interface ReviewRow {
  visitor_name: string;
  role: string | null;
  location: string | null;
  village: string | null;
  experience: string | null;
  experience_slug: string | null;
  rating: number | null;
  comment: string | null;
  avatar: string | null;
  visit_date: string | null;
  photos: unknown;
}

function mapReviewRow(row: ReviewRow): Testimonial {
  const photoGallery = asGallery(row.photos);
  return {
    name: row.visitor_name,
    role: row.role || "",
    location: row.location || "",
    quote: row.comment || "",
    experience: row.experience || "",
    experienceSlug: row.experience_slug || "",
    rating: row.rating ?? 5,
    avatar: row.avatar || "",
    date: formatMonth(row.visit_date),
    village: row.village || "",
    photos: photoGallery.map((image) => image.src),
    photoAlts: photoGallery.map((image) => image.alt),
  };
}

export async function fetchPublicReviews(): Promise<Testimonial[]> {
  try {
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .eq("status", "published")
      .order("id", { ascending: true });
    if (error) throw error;

    const rows = (data as ReviewRow[] | null) || [];
    if (rows.length === 0) return mockTestimonials;

    const mapped = rows.map(mapReviewRow);
    const paths = collectPaths([
      ...mapped.map((review) => review.avatar),
      ...mapped.flatMap((review) => review.photos),
    ]);
    const map = await resolveMap(paths);

    return mapped.map((review) => ({
      ...review,
      avatar: swap(review.avatar, map),
      photos: review.photos.map((photo) => swap(photo, map)),
    }));
  } catch {
    return mockTestimonials;
  }
}